/**
 * 聊天页标题区的视图推导（21am-3，人工给图）。纯函数，无 DOM/状态。
 *
 * 目标形态（人工指图，对齐旧版）：
 *   头像 ┃ 群名 [V2] [靓号]
 *        ┃ 281 位成员 · 557466
 *
 * 口径：
 * - **号码优先级 = 靓号 > 原始号**（与 contactsPageModel.displayAccountOf、
 *   FriendDetailPage 号码行、applyModel 同一口径）。靓号归一化复用
 *   vipBadge.normalizePremiumNo（去 `@`、字面量 "null" 当空，旧版踩出来的坑）。
 * - 分隔符 ` · `，**空段自动省略**：没拿到成员数就只显示号码，没拿到号码就只
 *   显示成员数，两样都没有则整条副标题不渲染（调用方按空串处理）。这保证了
 *   「数据没到位」时标题区不会出现孤零零的分隔点或空行。
 *
 * 为什么私聊的号码要从通讯录册反查：`content_list` 行（ConversationRemote）
 * **不下发 UserNo/PremiumNo**，只有 nickname/avatar/friendNote/vipLevel。
 * 为一行副标题去拉 friend/info 正是 14.38b 刻意省掉的那笔请求（requests 场景 C
 * 预算），所以沿用 presenceModel.offlineTimeOfFriend 同一条路子：册里有就显示，
 * 没有就不显示。根治要后端在 content_list 补字段，已记 backend-suggestions。
 */

import { normalizePremiumNo } from './vipBadge';
import type { ContactsBook } from './contactsModel';

/** 展示号码：靓号优先，否则原始号（<=0 视为无）；都没有返回空串 */
export function displayNoOf(premiumNo: string | null | undefined, rawNo: number): string {
  const premium = normalizePremiumNo(premiumNo);
  if (premium !== '') return premium;
  return rawNo > 0 ? String(rawNo) : '';
}

/** ` · ` 连接非空段；全空返回空串（调用方据此不渲染副标题） */
export function joinSubtitle(parts: ReadonlyArray<string>): string {
  return parts.filter((p) => p !== '').join(' · ');
}

/**
 * 群聊副标题：`N 位成员 · 群号`。
 * count<=0（快照未到/服务端计数异常）时省掉成员段，只剩号码。
 */
export function groupSubtitle(
  count: number,
  groupNo: number,
  premiumNo: string | null | undefined,
): string {
  return joinSubtitle([count > 0 ? `${count} 位成员` : '', displayNoOf(premiumNo, groupNo)]);
}

/**
 * 私聊副标题：`在线态 · 号码`。presenceText 由 presenceModel.presenceStatusText
 * 产出（未知态是空串，此时整段省掉——条目缺失≠离线，presenceModel 文件头口径）。
 */
export function dmSubtitle(presenceText: string, account: string): string {
  return joinSubtitle([presenceText, account]);
}

/**
 * 从通讯录册反查好友的展示号码（靓号优先）；册未加载或查不到返回空串。
 * 与 presenceModel.offlineTimeOfFriend 同一遍历口径（册规模是好友数，线性可接受；
 * 调用方须用 useMemo 按册引用缓存，别放进每帧都跑的选择器里）。
 */
export function friendAccountOf(book: ContactsBook | null, uid: number): string {
  if (book === null || uid <= 0) return '';
  for (const g of book.groups) {
    for (const f of g.friends) {
      if (f.friendId === uid) return displayNoOf(f.premiumNo, f.userNo);
    }
  }
  return '';
}

/**
 * 从通讯录册反查好友的显示名（备注 > 昵称 > username；册未加载/查不到空串）。
 *
 * 为什么标题要这条兜底：会话行的 nickname 是 `content_list` 下发或占位行建行
 * 那一刻的帧字段，**两者都可能是空**——占位行只有「对方发来」的帧才带昵称
 * （自己他端回显/信令建的行是空名），而 bumpConversation 之后不再回填（只
 * 展开 prev）。名字空了标题就只能显示 `dm:<uid>` 给用户看。号码段早就在走
 * 册反查（friendAccountOf），名字顺同一条路子兜一下即可，零新增请求。
 */
export function friendNameOf(book: ContactsBook | null, uid: number): string {
  if (book === null || uid <= 0) return '';
  for (const g of book.groups) {
    for (const f of g.friends) {
      if (f.friendId === uid) return f.friendNote || f.nickname || f.username;
    }
  }
  return '';
}
